import type { SimpleTableConfig } from "@/components/shared/UniversalCRMTable/types";
import type { RecurringCharge } from "@/lib/types/recurring-charges";
import { getRealmSelectOptions } from "@/lib/integrations/quickbooks/realms";
import { COMMON_QUICK_FILTERS } from "@/components/shared/config/quick-filters/common/common-quick-filters";
import { recurringChargeDetailActions, RECURRING_CHARGE_DETAIL_CONFIG } from "./RecurringChargeDetailConfig";
import {
  DollarSign,
  Calendar,
  Clock,
  Zap,
  Hash,
} from "lucide-react";

export const RECURRING_CHARGES_CONFIG: SimpleTableConfig<RecurringCharge> = {
  entity: "recurring-charges",
  displayName: "Recurring Charges",
  statusFieldId: "schedule_type",
  defaultPageSize: 10,
  defaultSort: { field: "next_charge_date", direction: "asc" },

  // Quick filters (pill bar): filter by QuickBooks billing company (WTC vs SM).
  // recurring_charges uses `realm_id`, so override the common filter's field.
  quickFilters: [{ ...COMMON_QUICK_FILTERS.realm, field: "realm_id" }],

  rowActionsPlacement: "inlineTitle",
  rowActionsMaxPrimaryIcons: 2,
  rowActionsDisplayMode: "iconOnly",

  mobileDetailSections: [
    { id: "customer", label: "Customer" },
    { id: "charge", label: "Charge Details" },
    { id: "schedule", label: "Schedule" },
    { id: "billing", label: "Billing Account" },
  ],
  mobileDetailUnsectionedLabel: "Other",

  /** Default table column order: billing company → customer → schedule → next charge → last charge */
  tableColumnOrder: [
    "realm_id",
    "customer_id",
    "schedule_type",
    "next_charge_date",
    "last_charged_at",
  ],

  fields: {
    customer_id: {
      type: "client-select",
      label: "Customer",
      isForeignKey: true,
      foreignKey: {
        referencedTable: "contacts",
        referencedColumn: "id",
        constraintName: "recurring_charges_customer_id_fkey",
        onDelete: "CASCADE",
        onUpdate: "NO ACTION",
        relationKind: "many-to-one",
      },
      sortable: true,
      filterable: true,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 250,
      minWidth: 200,
      maxWidth: 400,
      placeholder: "Select customer",
      icon: "User",
      mobileDetailSectionId: "customer",
    },
    schedule_type: {
      type: "select",
      label: "Schedule",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 130,
      minWidth: 100,
      maxWidth: 150,
      options: [
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
      ],
      icon: "Calendar",
      groupable: true,
      mobileDetailSectionId: "charge",
    },
    last_charged_at: {
      type: "date",
      label: "Last Charged",
      sortable: true,
      filterable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      icon: "Clock",
      mobileDetailSectionId: "schedule",
    },
    next_charge_date: {
      type: "date",
      label: "Next Charge Date",
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "dateRange",
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      icon: "Calendar",
      mobileDetailSectionId: "schedule",
    },
    metadata: {
      type: "json",
      label: "Charge Details",
      sortable: false,
      filterable: false,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 300,
      minWidth: 250,
      maxWidth: 500,
      mobileDetailSectionId: "charge",
    },
    realm_id: {
      type: "select",
      label: "Billing Company",
      sortable: true,
      filterable: true,
      searchable: false,
      resizable: true,
      hideable: true,
      editable: false,
      filterType: "exact",
      width: 170,
      minWidth: 140,
      maxWidth: 220,
      options: getRealmSelectOptions(),
      groupable: true,
      mobileDetailSectionId: "billing",
    },
    admin_email: {
      type: "text",
      label: "Admin Email",
      sortable: false,
      filterable: false,
      searchable: true,
      resizable: true,
      hideable: true,
      editable: false,
      width: 200,
      minWidth: 150,
      maxWidth: 300,
      mobileDetailSectionId: "billing",
    },
    created_at: {
      type: "date",
      label: "Created",
      sortable: true,
      filterable: false,
      resizable: true,
      hideable: true,
      editable: false,
      width: 150,
      minWidth: 120,
      maxWidth: 200,
      mobileDetailSectionId: "schedule",
    },
  },

  features: {
    search: true,
    filters: true,
    export: true,
    bulkActions: true,
    columnResizing: true,
    columnVisibility: true,
    savedViews: true,
    infiniteScroll: true,
    multiColumnSort: true,
    calendarView: true,
  },

  actions: {
    create: false,
    edit: false,
    delete: false,
    view: true,
  },

  // Add detail page actions for table rows
  detailActions: recurringChargeDetailActions,

  customFilterHooks: {},
};
